import { useEffect, useRef } from 'react';
import { normalizeCartLines } from '@shopgnd/shared';
import { useCartStore } from '@/stores/cartStore';
import { useAuthStore } from '@/stores/authStore';
import { upsertCart, clearCart } from '@/api/cart';

/**
 * Debounced cart-to-server sync for signed-in users.
 * Enables abandoned-cart recovery emails when the user leaves items for 2+ hours.
 */
export function useCartSync() {
  const lines = useCartStore((s) => normalizeCartLines(s.lines));
  const user = useAuthStore((s) => s.user);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!user) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const total = lines.reduce((s, l) => s + l.priceCents * l.quantity, 0);
      if (lines.length === 0) {
        void clearCart(user.id);
      } else {
        void upsertCart(user.id, user.email, lines, total);
      }
    }, 2000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [lines, user]);
}
